import { useQuery } from '@tanstack/react-query'
import { cachePatch, useOptimisticMutation } from '@/lib/optimistic-mutation'
import { useMemo } from 'react'
import api from '@/lib/api'
import { cacheSync } from '@/lib/cacheSync'
import type { GradeLevel } from '@/types/api'
import {
  formatCourseGradeLevels,
  formatGradeLevelSpan,
  formatGradeLevels,
  gradeLevelLabel,
  type HasGradeLevels,
} from '@/lib/gradeLevels'
import { gradeLevelKeys } from '@/lib/queryKeys'
import { REFERENCE_DATA_STALE_TIME } from '@/lib/staleTimes'

/**
 * Re-exported from `lib/queryKeys`, which owns the whole key space — see the note
 * there. A query still names its key from the module it belongs to.
 */
export { gradeLevelKeys }

/**
 * The school's configured grade levels - what it runs and what it calls them.
 *
 * Every grade-level picker in the app reads through this instead of a hardcoded
 * list, so a school that renames Grade 7 to Year 7, or that runs no upper school,
 * sees that in the student form, the course form and the intake sidebar at once.
 */

export function useGradeLevels() {
  return useQuery({
    queryKey: gradeLevelKeys.list(),
    queryFn: async (): Promise<GradeLevel[]> => {
      const res = await api.get<{ gradeLevels: GradeLevel[] }>('/api/grade-levels')
      return res.data.gradeLevels
    },
    // Grade levels change about once a year. Refetching them on every mount of
    // every form that has a grade picker is pure noise.
    staleTime: REFERENCE_DATA_STALE_TIME,
  })
}

/**
 * The grades a *new* record may be put in, as `<Select>` options.
 *
 * Active only: a retired grade still has to render on the students already in it,
 * which is what `useGradeLevelLabels` below is for, but it must not be offered.
 * Values are strings because that is what a Radix `<Select>` deals in.
 */
export function useGradeLevelOptions(): {
  options: Array<{ value: string; label: string }>
  isLoading: boolean
} {
  const { data, isLoading } = useGradeLevels()
  const options = useMemo(
    () =>
      (data ?? [])
        .filter((level) => level.isActive)
        .map((level) => ({ value: String(level.level), label: level.label })),
    [data]
  )
  return { options, isLoading }
}

/**
 * A `level -> label` lookup covering retired grades too.
 *
 * Undefined while the query is in flight, which callers read as "fall back to the
 * built-in wording" rather than as "no label" - a grade chip that flickers from
 * blank to "Grade 7" is worse than one that starts at "Grade 7" and stays.
 */
export function useGradeLevelLabels(): Map<number, string> | undefined {
  const { data } = useGradeLevels()
  return useMemo(
    () => (data ? new Map(data.map((level) => [level.level, level.label])) : undefined),
    [data]
  )
}

/**
 * The `lib/gradeLevels` formatters, already carrying the school's own words.
 *
 * Components take this rather than importing the bare functions so that renaming a
 * grade in Settings changes the student list, the course catalogue and the
 * enrolment picker together. Before the query resolves the formatters fall back to
 * the built-in wording, which is what they always did.
 */
export function useGradeLevelFormat() {
  const labels = useGradeLevelLabels()
  return useMemo(
    () => ({
      label: (level: number) => gradeLevelLabel(level, labels),
      formatLevels: (levels: number[] | null | undefined) => formatGradeLevels(levels, labels),
      formatSpan: (levels: number[] | null | undefined) => formatGradeLevelSpan(levels, labels),
      // For a Grade Levels cell: resolves the legacy scalar before formatting, so
      // an unmigrated course reads as the grade it is taught in rather than "All
      // grades".
      formatCourse: (course: HasGradeLevels) => formatCourseGradeLevels(course, labels),
      labels,
    }),
    [labels]
  )
}

export interface CreateGradeLevelInput {
  level: number
  label: string
  shortLabel?: string
  band?: string | null
  isActive?: boolean
}

export function useCreateGradeLevel() {
  return useOptimisticMutation({
    mutationFn: async (input: CreateGradeLevelInput): Promise<GradeLevel> => {
      const res = await api.post<{ gradeLevel: GradeLevel }>('/api/grade-levels', input)
      return res.data.gradeLevel
    },
    // No optimistic row: the server fills in `shortLabel` and the rest of the
    // defaults, so a locally invented one would be replaced by a different row a
    // moment later. The awaited invalidation still means the caller never sees
    // the list without it.
    invalidateKeys: cacheSync.gradeLevels(),
  })
}

export interface UpdateGradeLevelInput {
  level: number
  label?: string
  shortLabel?: string
  band?: string | null
  isActive?: boolean
}

export function useUpdateGradeLevel() {
  return useOptimisticMutation({
    mutationFn: async ({ level, ...changes }: UpdateGradeLevelInput): Promise<GradeLevel> => {
      const res = await api.patch<{ gradeLevel: GradeLevel }>(
        `/api/grade-levels/${level}`,
        changes
      )
      return res.data.gradeLevel
    },
    // Keyed on `level`, not an id: that is this table's primary key, and it is
    // what identifies the row rather than being one of the editable fields.
    // Retiring a grade is the common edit here, and it moves the grade out of
    // every picker in the app at once — worth having repaint immediately.
    optimistic: ({ level, ...changes }) => [
      cachePatch<GradeLevel[]>(gradeLevelKeys.list(), (levels) =>
        levels.map((gradeLevel) =>
          gradeLevel.level === level ? { ...gradeLevel, ...changes } : gradeLevel
        )
      ),
    ],
    invalidateKeys: cacheSync.gradeLevels(),
  })
}

export function useDeleteGradeLevel() {
  return useOptimisticMutation({
    mutationFn: async (level: number): Promise<void> => {
      await api.delete(`/api/grade-levels/${level}`)
    },
    // A real delete, not a retirement: the endpoint refuses with a 409 when any
    // student or course still sits in the grade, so a row that survives the call
    // is genuinely gone. The refusal path rolls the row back.
    optimistic: (level) => [
      cachePatch<GradeLevel[]>(gradeLevelKeys.list(), (levels) =>
        levels.filter((gradeLevel) => gradeLevel.level !== level)
      ),
    ],
    invalidateKeys: cacheSync.gradeLevels(),
  })
}
